import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOptions } from '../../utils/sortListings';

// Icon-only sort control (#15). Replaces the old "Sort By" labelled dropdown
// with the app's sort icon; clicking it opens a small menu of sort options.
// Icon source: iOS app asset (sort-alt) copied to assets/images/icons/sort.svg.
//
// [CF 2026-08-02] The menu now carries the SAME set the iOS app offers —
// Newest/Oldest and Distance everywhere, plus Price on the categories whose
// rows actually have one (`withPrice`). Labels (incl. the Arabic أ - ي pair)
// live in `utils/sortListings` so the app, the web menu and the API's `sort`
// param all use one vocabulary.
export default function SortControl({ value, onChange, options, withPrice = false }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  // [CF 2026-08-02] The menu is anchored to a 42px button that can sit hard
  // against either edge of the screen, so a fixed left/right anchor always
  // clipped it somewhere (client: the sort list ran off the page). Measure once
  // it is on screen and nudge it back inside the viewport instead.
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const pad = 8;
    const r = el.getBoundingClientRect();
    let dx = 0;
    if (r.right > window.innerWidth - pad) dx = window.innerWidth - pad - r.right;
    if (r.left + dx < pad) dx = pad - r.left;
    setShift(dx);
  }, [open]);

  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const opts = options || sortOptions({ isRTL, withPrice });

  // Close when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', flex: '0 0 auto' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('Sort By')}
        title={t('Sort By')}
        style={{
          width: '42px',
          height: '42px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: '50%',
          background: value ? '#fff7e6' : 'transparent',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <img
          src="assets/images/icons/sort.svg"
          alt={t('Sort By')}
          style={{ width: '21px', height: '23px' }}
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            insetInlineStart: 0,
            transform: shift ? `translateX(${shift}px)` : undefined,
            zIndex: 50,
            minWidth: '180px',
            maxWidth: 'calc(100vw - 16px)',
            background: '#fff',
            border: '1px solid #e1e5ea',
            borderRadius: '10px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }}
        >
          {opts.map((o) => {
            const active = (value || '') === o.value;
            return (
              <button
                key={o.value || 'default'}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'start',
                  padding: '10px 16px',
                  border: 'none',
                  background: active ? '#fff7e6' : '#fff',
                  color: active ? '#2d343b' : '#444',
                  fontWeight: active ? 700 : 400,
                  font: 'inherit',
                  cursor: 'pointer',
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
